import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { SuspensionDetailModel } from 'src/app/models/rental/suspension-detail.model';
import { SuspensionModel } from 'src/app/models/rental/suspension.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { SuspensionDetailService } from 'src/app/services/rental/suspension-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-suspension-detail',
  templateUrl: './register-suspension-detail.component.html',
  styleUrls: ['./register-suspension-detail.component.scss'],
  standalone: false
})
export class RegisterSuspensionDetailComponent implements OnInit {

  @Input() disabled: boolean = false;
  @Input() suspensionDetails: SuspensionDetailModel[] = [];
  @Input() suspensionContract: SuspensionModel = new SuspensionModel();
  @Input() suspensionDetailContract: SuspensionDetailModel = new SuspensionDetailModel();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  codeNameArticle: string = '';
  formSuspensionDetail: FormGroup = new FormGroup({});
  suspensionDetailId: number = 0;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private suspensionDetailService: SuspensionDetailService,
    private toastHandlerService: ToastHandlerService) { }

  ngOnInit() {
    this.validateForm(this.suspensionDetailContract);
  }

  async ngOnChanges(changes: SimpleChanges) {
    const suspensionDetails = changes["suspensionDetails"];
    const suspensionDetailContract = changes["suspensionDetailContract"];
    if (suspensionDetails && suspensionDetails.currentValue.length > 1) {
      this.createNewSupensionDetail(suspensionDetails.currentValue);
      return;
    }
    if (suspensionDetailContract && !suspensionDetailContract.firstChange) {
      if (this.suspensionDetailContract.suspensionDetailId === 0) {
        this.suspensionDetailId = 0;
      } else {
        this.suspensionDetailId = this.suspensionDetailContract.suspensionDetailId;
      }
      this.isFormView.emit(true);
      this.validateForm(this.suspensionDetailContract);
    }
    if (this.disabled) {
      setTimeout(() => this.formSuspensionDetail.disable());
    }
  }

  async getArticle(articleId: number | null) {
    try {
      if (articleId != null && articleId != 0) {
        const article = await firstValueFrom(this.articleService.getArticleById(articleId));
        if (article) {
          this.codeNameArticle = `${article.code} - ${article.name}`;
        } else {
          this.codeNameArticle = 'Error al buscar el elemento. Por favor refresque la página.';
        }
      } else {
        this.codeNameArticle = '';
      }
    } catch (error) {
      console.error('Error while getting the article.', error);
    }
  }

  clearForm() {
    this.codeNameArticle = '';
    const modelSuspensionDetail = new SuspensionDetailModel;
    this.validateForm(modelSuspensionDetail)
    this.isFormView.emit(false);
  }

  async createNewSupensionDetail(suspensionDetails: SuspensionDetailModel[]) {
    try {
      await firstValueFrom(this.suspensionDetailService.createSomeDetails(suspensionDetails, this.suspensionContract.suspensionId));
      this.toastHandlerService.showToast('success', 'El detalle de la suspensión fue creado exitosamente.', '');
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error while creating the suspension detail.', error);
    }
  }

  async send() {
    Object.keys(this.formSuspensionDetail.controls).forEach(key => {
      const control = this.formSuspensionDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formSuspensionDetail.valid) {
      const modelSuspensionDetail = this.formSuspensionDetail.value as SuspensionDetailModel;
      this.suspensionDetailContract.days = modelSuspensionDetail.days;
      this.suspensionDetailContract.quantity = modelSuspensionDetail.quantity;
      if (this.suspensionDetailId == 0) {
        this.createNewSupensionDetail([this.suspensionDetailContract]);
        this.clearForm();
      } else {
        try {
          await firstValueFrom(this.suspensionDetailService.updateSuspensionDetail(this.suspensionDetailContract));
          this.toastHandlerService.showToast('success', 'El detalle de la suspensión fue modificado exitosamente.', '');
          this.suspensionDetailContract = new SuspensionDetailModel();
          this.clearForm();
          this.isFormView.emit(false);
          this.updateListDetail.emit(true);
        } catch (error) {
          console.error('Error while updating the suspension detail.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      suspensionId: {
        required: 'El campo Suspensión es obligatorio.',
        min: 'Debes seleccionar una Suspensión válida.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
      },
      days: {
        required: 'El campo Días es obligatorio.',
      }
    };

    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formSuspensionDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async validateForm(suspensionDetail: SuspensionDetailModel) {
    this.formSuspensionDetail = this.formBuilder.group({
      suspensionId: [this.suspensionContract.suspensionId, [Validators.required, Validators.min(1)]],
      articleId: [suspensionDetail.articleId, [Validators.required, Validators.min(1)]],
      quantity: [suspensionDetail.quantity, [Validators.required]],
      days: [suspensionDetail.days, [Validators.required]],
    });
    if (suspensionDetail.suspensionId != 0) {
      await this.getArticle(suspensionDetail.articleId);
    }
  }
}
